<script lang="ts">
    import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import { REVIEW_TYPE } from "@apps/reviews/constants";
    import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import { reviewsFormModalToken } from "@apps/reviews/tokens";
    import AttributesQuery from "@apps/reviews/widgets/review_form_modal_widget/queries/AttributesQuery";
    import CanLeaveReviewQuery from "@apps/reviews/widgets/review_form_modal_widget/queries/CanLeaveReviewQuery";
    import CanLeaveReviewService from "@apps/reviews/widgets/review_form_modal_widget/services/CanLeaveReviewService";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
    import WidgetClosedSdkEvent from "@modules/sdk/common_sdk/events/WidgetClosedSdkEvent";
    import WidgetOpenedSdkEvent from "@modules/sdk/common_sdk/events/WidgetOpenedSdkEvent";
    import { dispatchSdkEvent } from "@modules/sdk/helpers";
    import ShopifyAnalyticsManager from "@modules/shopify_analytics/ShopifyAnalyticsManager";
    import { lastValueFrom, map } from "rxjs";
    import { onMount } from "svelte";

    import ManagedModalWithDialog from "@components/Modal/ManagedModalWithDialog.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import ModalManagerProvider from "@components/Modal/ModalManager/ModalManagerProvider.svelte";
    import Step from "@components/Stepper/Step.svelte";
    import Stepper from "@components/Stepper/Stepper.svelte";
    import StepperController from "@components/Stepper/StepperController";
    import { LoggerInterface } from "@interfaces/LoggerInterface";

    import ReviewFormBottomNavigation from "./components/FormNavigation/ReviewsFormBottomNavigation.svelte";
    import ReviewsFormTopNavigation from "./components/FormNavigation/ReviewsFormTopNavigation.svelte";
    import AttributesStep from "./components/ReviewsFormSteps/AttributesStep/AttributesStep.svelte";
    import GuestInfoStep from "./components/ReviewsFormSteps/GuestInfoStep/GuestInfoStep.svelte";
    import MediaStep from "./components/ReviewsFormSteps/MediaStep/MediaStep.svelte";
    import RatingStep from "./components/ReviewsFormSteps/RatingStep/RatingStep.svelte";
    import ResultStep from "./components/ReviewsFormSteps/ResultStep/ResultStep.svelte";
    import ReviewStep from "./components/ReviewsFormSteps/ReviewTextStep/ReviewTextStep.svelte";
    import { LEAVE_REVIEW_SOURCE, REVIEWS_FORM_STEPS_NAMES } from "./constants";
    import ReviewsWizardManager from "./managers/ReviewsWizardManager";
    import LeaveReviewEarningRuleQuery from "./queries/LeaveReviewEarningRuleQuery";
    import LeaveReviewQuery from "./queries/LeaveReviewQuery";
    import PurchasedProductsQuery from "./queries/PurchasedProductsQuery";
    import RewardForReviewQuery from "./queries/RewardForReviewQuery";
    import AttributesService from "./services/AttributesService";
    import LeaveReviewEarningRuleService from "./services/LeaveReviewEarningRuleService";
    import LeaveReviewService from "./services/LeaveReviewService";
    import PurchasedProductsService from "./services/PurchasedProductsService";
    import RewardForReviewService from "./services/RewardForReviewService";

    export let modalManager: ModalManager;
    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let canLeaveReviewService: CanLeaveReviewService;
    export let canLeaveReviewQuery: CanLeaveReviewQuery;
    export let leaveReviewService: LeaveReviewService;
    export let leaveReviewQuery: LeaveReviewQuery;
    export let attributesService: AttributesService;
    export let attributesQuery: AttributesQuery;
    export let leaveReviewEarningRuleQuery: LeaveReviewEarningRuleQuery;
    export let leaveReviewEarningRuleService: LeaveReviewEarningRuleService;
    export let rewardForReviewQuery: RewardForReviewQuery;
    export let rewardForReviewService: RewardForReviewService;
    export let productsService: ProductsServiceInterface;
    export let purchasedProductsQuery: PurchasedProductsQuery;
    export let purchasedProductsService: PurchasedProductsService;
    export let logger: LoggerInterface;
    export let shopifyAnalyticsManager: ShopifyAnalyticsManager | null;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let afterWidgetInject: () => void;

    const reviewsSettings$ =
        reviewsStorefrontValuesService.selectReviewsSettings();
    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    modalManager.addModal(reviewsFormModalToken);
    const isModalOpen$ = modalManager.selectIsOpen(reviewsFormModalToken);
    const modalParams$ = modalManager.selectParams(reviewsFormModalToken);

    let source: LEAVE_REVIEW_SOURCE =
        LEAVE_REVIEW_SOURCE.REVIEWS_PRODUCT_PAGE_WIDGET;
    let reviewType: REVIEW_TYPE | undefined;
    let orderId: number | undefined;
    let reviewRequestToken: string | undefined;
    let showPurchasedProducts = false;
    let productId: number | undefined;
    let isTestReview = false;
    let onLeaveReviewCb: ((review: ReviewEntity) => void) | undefined;
    const leaveReviewCount$ = leaveReviewQuery.leaveReviewCount$;

    $: {
        if ($modalParams$) {
            source = $modalParams$.source;
            reviewType = $modalParams$.reviewType;
            orderId = $modalParams$.orderId;
            reviewRequestToken = $modalParams$.reviewRequestToken;
            showPurchasedProducts = !!$modalParams$.showPurchasedProducts;
            productId = $modalParams$.productId;
            onLeaveReviewCb = $modalParams$.onLeaveReviewCb;
            isTestReview =
                $modalParams$.reviewRequestToken ===
                "fake-review-request-token";
        }
    }

    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));
    $: isAuthenticated = Boolean($isCustomer$ || reviewRequestToken);

    const hasAttributes$ = attributesQuery.hasAttributes$;

    $: isLeaveReviewPageWidget =
        source === LEAVE_REVIEW_SOURCE.LEAVE_REVIEW_PAGE_WIDGET;

    const stepperController = new StepperController();

    const reviewsWizardManager = new ReviewsWizardManager({
        attributes$: attributesQuery.attributes$,
        reviewsSettings$: reviewsSettings$,
        reviewType: reviewType,
        onSubmitCollectedData: (data) => {
            if (isTestReview) {
                stepperController.nextStep();
                return;
            }

            const body = {
                ...data,
                widget: source,
                productId: productId,
                reviewRequestToken: reviewRequestToken,
            };
            lastValueFrom(leaveReviewService.leaveReview(body))
                .then((review) => {
                    onLeaveReviewCb && onLeaveReviewCb(review);
                    return review;
                })
                .then((review) => {
                    const leaveReviewEarningRule =
                        leaveReviewEarningRuleQuery.getValue()
                            .leaveReviewEarningRule;
                    if (
                        isAuthenticated &&
                        leaveReviewEarningRule &&
                        review?.isPublished
                    ) {
                        lastValueFrom(
                            rewardForReviewService.getRewardForReviewByInterval(
                                review.id,
                                reviewRequestToken
                            )
                        );
                    }

                    if (
                        source ===
                            LEAVE_REVIEW_SOURCE.LEAVE_REVIEW_PAGE_WIDGET &&
                        $leaveReviewCount$ < 2
                    ) {
                        showPurchasedProducts = true;
                    } else {
                        showPurchasedProducts = false;
                    }

                    if (shopifyAnalyticsManager && data.guestEmail) {
                        shopifyAnalyticsManager.sendVisitorInfo({
                            email: data.guestEmail,
                        });
                    }

                    reviewsWizardManager.resetForms();
                })
                .catch((err) => {
                    logger.error(err.message);
                })
                .finally(() => {
                    stepperController.nextStep();
                });
        },
    });

    onMount(() => {
        const isModalOpenSubscription = isModalOpen$.subscribe((isOpen) => {
            if (isOpen) {
                setTimeout(() => {
                    dispatchSdkEvent(
                        new WidgetOpenedSdkEvent(
                            REVIEWS_WIDGET_NAMES.REVIEWS_FORM_MODAL_WIDGET
                        )
                    );
                }, 0);
            } else {
                dispatchSdkEvent(
                    new WidgetClosedSdkEvent(
                        REVIEWS_WIDGET_NAMES.REVIEWS_FORM_MODAL_WIDGET
                    )
                );
            }
        });

        afterWidgetInject();

        return () => {
            isModalOpenSubscription.unsubscribe();
            reviewsWizardManager.destroy();
        };
    });

    const getAttributes = () => {
        if (!productId) return;

        attributesService.resetAttributesStore();
        lastValueFrom(attributesService.getAttributesByProductId(productId));
    };

    $: {
        productId;
        getAttributes();
    }

    $: {
        reviewType;
        reviewsWizardManager.setReviewType(reviewType);
    }

    const getLeaveReviewEarningRule = () => {
        if (isAuthenticated || reviewRequestToken) {
            lastValueFrom(
                leaveReviewEarningRuleService.getLeaveReviewEarningRule({
                    productId,
                    reviewRequestToken,
                })
            );
        }
    };

    $: {
        isAuthenticated;
        productId;
        reviewRequestToken;
        getLeaveReviewEarningRule();
    }

    const addProductForFetching = () => {
        if (isLeaveReviewPageWidget && productId) {
            productsService.addProductForFetching(productId);
        }
    };

    $: {
        isLeaveReviewPageWidget;
        productId;
        addProductForFetching();
    }

    const getPurchasedProducts = () => {
        if (showPurchasedProducts && isAuthenticated) {
            lastValueFrom(
                purchasedProductsService.getPurchasedProducts({
                    orderId,
                    reviewRequestToken,
                })
            ).then((purchasedProducts) => {
                if (purchasedProducts.length === 0) {
                    reviewType = REVIEW_TYPE.SITE_REVIEW;
                    showPurchasedProducts = false;
                }
            });
        }
    };

    $: {
        showPurchasedProducts;
        getPurchasedProducts();
    }

    const checkIfCanLeaveReview = () => {
        if (showPurchasedProducts && reviewType === REVIEW_TYPE.PRODUCT_REVIEW)
            return;

        if (reviewType === REVIEW_TYPE.PRODUCT_REVIEW) {
            lastValueFrom(
                canLeaveReviewService.checkIsCanLeaveReview({
                    productId,
                    reviewRequestToken,
                })
            );
        } else if (reviewRequestToken) {
            lastValueFrom(
                canLeaveReviewService.checkIsCanLeaveReview({
                    reviewRequestToken,
                })
            );
        } else {
            lastValueFrom(canLeaveReviewService.checkIsCanLeaveReview());
        }
    };

    $: {
        $modalParams$;
        productId;
        checkIfCanLeaveReview();
    }

    $: {
        source;
        reviewsWizardManager.resetForms();
        stepperController.resetAnimation();
        stepperController.resetCurrentStep();
    }

    const handleCloseModal = () =>
        modalManager.closeModal(reviewsFormModalToken);

    const currentStepName$ = stepperController.currentStep$.pipe(
        map((step) => step?.name as REVIEWS_FORM_STEPS_NAMES)
    );

    $: hideModalCloseButton = [
        REVIEWS_FORM_STEPS_NAMES.REVIEW,
        REVIEWS_FORM_STEPS_NAMES.ATTRIBUTES,
        REVIEWS_FORM_STEPS_NAMES.MEDIA,
        REVIEWS_FORM_STEPS_NAMES.GUEST_INFO,
        REVIEWS_FORM_STEPS_NAMES.RESULT,
    ].includes($currentStepName$);

    $: disableModalCloseAction =
        [
            REVIEWS_FORM_STEPS_NAMES.REVIEW,
            REVIEWS_FORM_STEPS_NAMES.MEDIA,
            REVIEWS_FORM_STEPS_NAMES.ATTRIBUTES,
            REVIEWS_FORM_STEPS_NAMES.GUEST_INFO,
        ].includes($currentStepName$) || isLeaveReviewPageWidget;

    const handleUpdateProductId = (
        event: CustomEvent<{ productId: number }>
    ) => {
        reviewsWizardManager.resetForms();
        productId = event.detail.productId;
        reviewType = REVIEW_TYPE.PRODUCT_REVIEW;
    };

    const handleUpdateShowPurchasedProducts = (
        event: CustomEvent<{ value: boolean }>
    ) => {
        showPurchasedProducts = event.detail.value;
    };
</script>

{#if $reviewsBranding$ && $reviewsSettings$ && $modalParams$}
    <ReviewsTheme branding={$reviewsBranding$}>
        <ModalManagerProvider {modalManager}>
            <ManagedModalWithDialog
                modalToken={reviewsFormModalToken}
                modalClass="gw-rv-form-modal"
                dialogClass="gw-rv-form-modal__dialog"
                modalBodyClass="gw-rv-form-modal__body"
                disableCloseButton={isLeaveReviewPageWidget ||
                    hideModalCloseButton}
                disableClose={disableModalCloseAction}
                disablePortal
                on:close={handleCloseModal}
                disableFocusTrap={isLeaveReviewPageWidget}
            >
                <div class="gw-rv-form-modal__steps">
                    <ReviewsFormTopNavigation {reviewsWizardManager} />
                    <Stepper {stepperController}>
                        <Step name={REVIEWS_FORM_STEPS_NAMES.RATING} order={1}>
                            <RatingStep
                                {productId}
                                {canLeaveReviewQuery}
                                {reviewsWizardManager}
                                {purchasedProductsQuery}
                                {showPurchasedProducts}
                                {isAuthenticated}
                                showPurchasedProductsInitial={$modalParams$?.showPurchasedProducts ||
                                    false}
                                {source}
                                {reviewType}
                                on:updateProductId={handleUpdateProductId}
                                on:updateShowPurchasedProducts={handleUpdateShowPurchasedProducts}
                            />
                        </Step>

                        <Step name={REVIEWS_FORM_STEPS_NAMES.REVIEW} order={2}>
                            <ReviewStep
                                {isAuthenticated}
                                {leaveReviewEarningRuleQuery}
                                reviewsSettings={$reviewsSettings$}
                                {reviewsWizardManager}
                            />
                        </Step>

                        {#if $hasAttributes$}
                            <Step
                                name={REVIEWS_FORM_STEPS_NAMES.ATTRIBUTES}
                                order={3}
                            >
                                <AttributesStep
                                    {reviewsWizardManager}
                                    {attributesQuery}
                                />
                            </Step>
                        {/if}

                        {#if $reviewsSettings$.isReviewsMediaEnabled}
                            <Step
                                name={REVIEWS_FORM_STEPS_NAMES.MEDIA}
                                order={4}
                            >
                                <MediaStep
                                    {reviewsWizardManager}
                                    {leaveReviewEarningRuleQuery}
                                />
                            </Step>
                        {/if}

                        {#if !isAuthenticated}
                            <Step
                                name={REVIEWS_FORM_STEPS_NAMES.GUEST_INFO}
                                order={5}
                            >
                                <GuestInfoStep
                                    {reviewsWizardManager}
                                    reviewsSettings={$reviewsSettings$}
                                    {isAuthenticated}
                                />
                            </Step>
                        {/if}

                        <Step name={REVIEWS_FORM_STEPS_NAMES.RESULT} order={6}>
                            <ResultStep
                                {leaveReviewQuery}
                                {rewardForReviewQuery}
                                {leaveReviewEarningRuleService}
                                {rewardForReviewService}
                                {reviewsWizardManager}
                                {isAuthenticated}
                                {canLeaveReviewService}
                                {purchasedProductsQuery}
                                {source}
                                {showPurchasedProducts}
                                {isTestReview}
                            />
                        </Step>
                    </Stepper>
                    <ReviewFormBottomNavigation
                        reviewsSettings={$reviewsSettings$}
                        {isAuthenticated}
                        {reviewType}
                        {reviewsWizardManager}
                        {leaveReviewQuery}
                        {attributesQuery}
                        {stepperController}
                    />
                </div>
            </ManagedModalWithDialog>
        </ModalManagerProvider>
    </ReviewsTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../themes/globals.css";
    @import "../../../../themes/default/default.css";

    .gw-rv-form-modal {
        --gw-file-uploader-height: 7em;
        --gw-media-uploader-preview-padding: 4px;
        --gw-media-uploader-preview-list-gap: 6px;
        --gw-media-uploader-preview-item-width: calc(
            (100% - var(--gw-media-uploader-preview-list-gap) * 2) / 3 - 0.5px
        );

        .gw-modal-dialog.gw-rv-form-modal__dialog {
            padding: 0;
            width: 100%;
            max-width: 810px;
            overflow: hidden;

            @media screen and (max-width: 600px) {
                width: 100%;
                min-height: 100%;
                height: auto;
                margin: 0;
                border-radius: 0;
                display: flex;
                flex-direction: column;
            }
        }

        .gw-modal-body.gw-rv-form-modal__body {
            flex-grow: 1;
            width: 100%;
            padding: 0;

            @media screen and (max-width: 800px) {
                padding: 0 1em;
                box-sizing: border-box;
            }

            @media screen and (max-width: 600px) {
                width: 100%;
                min-height: 100%;
                height: auto;
                display: flex;
                flex-direction: column;
            }
        }

        &__steps {
            display: flex;
            flex-direction: column;
            height: 680px;
            justify-content: space-between;
            transition: height 1s ease;

            @media screen and (max-width: 600px) {
                flex-grow: 1;
            }
        }

        &__spinner {
            display: flex;
            height: 680px;
            justify-content: center;
            align-items: center;

            @media screen and (max-width: 600px) {
                flex-grow: 1;
            }
        }

        &__empty {
            height: 680px;
        }
    }
</style>
